import { useState, useEffect, useCallback, useRef } from 'react';
import {
  API_GET_WEB_TEAMS_DASHBOARDS_V2,
  API_DELETE_WEB_TEAM
} from '../../../../api/webHompageConfigurationServices';
import { API_GET_WEB_TEAMS_LAUNCHBAR_V2 } from '../../../../api/webLaunchBarConfigurationServices';
import { FEATURE_KEYS } from '../../../../config/featureRegistry';
import { useFeatureListData } from '../../../../hooks/useFeatureListData';
import { useTeamsData } from '../../../../hooks/useTeamsData';
import { deletedWebTeamsFromPlatform } from '../../../../utils/globalUtils';

// Teams for which a cleanup DELETE is already in flight (module-scoped so the
// dashboards and launchers hooks don't both delete the same team).
const inFlightTeamDeletes = new Set();

/**
 * Cleanup assignments for teams deleted from the platform.
 * Compares the given team assignments against the platform teams hierarchy
 * and calls API_DELETE_WEB_TEAM for each team that no longer exists
 * (the org/default team is excluded). Runs once per mount, only after both
 * teams and assignments have loaded. Best-effort: errors never block the page.
 *
 * @param {Array} teamAssignments — array of { teamId, ... } assignment entries
 * @param {Function} refetch — called after successful cleanup to reload data
 */
function useDeletedWebTeamsCleanup(teamAssignments, refetch) {
  const { teamsLinear, orgCode } = useTeamsData();
  const hasCleanedUp = useRef(false);

  useEffect(() => {
    if (hasCleanedUp.current || !teamsLinear.length || !orgCode || !teamAssignments?.length) return;

    const teamsMap = Object.fromEntries(teamsLinear.map((team) => [team.teamId, team]));
    const assignmentsByTeamId = Object.fromEntries(
      teamAssignments.map((item) => [item.teamId, item])
    );
    const deletedTeamIds = deletedWebTeamsFromPlatform(
      teamsMap,
      orgCode,
      assignmentsByTeamId
    ).filter((teamId) => !inFlightTeamDeletes.has(teamId));

    hasCleanedUp.current = true;
    if (!deletedTeamIds.length) return;

    deletedTeamIds.forEach((teamId) => inFlightTeamDeletes.add(teamId));
    Promise.all(deletedTeamIds.map((teamId) => API_DELETE_WEB_TEAM(teamId)))
      .then(() => refetch())
      .catch(() => {})
      .finally(() => {
        deletedTeamIds.forEach((teamId) => inFlightTeamDeletes.delete(teamId));
      });
  }, [teamsLinear, orgCode, teamAssignments, refetch]);
}

/**
 * Hook to fetch and normalize WebTeams dashboards data (V2 API)
 * Fetches both the actual dashboards and their team assignments,
 * and cleans up assignments for teams deleted from the platform.
 */
export function useWebTeamsDashboards() {
  // Get all dashboards via feature registry
  const { itemsById: dashboardsById, refetch: refetchDashboards } = useFeatureListData(
    FEATURE_KEYS.WEB_HOMEPAGE
  );

  const [state, setState] = useState({
    dashboards: null,
    loading: true
  });

  const fetchData = useCallback(async ({ silent = true } = {}) => {
    if (!silent) setState((prev) => ({ ...prev, loading: true }));
    try {
      const response = await API_GET_WEB_TEAMS_DASHBOARDS_V2();

      if (response?.data) {
        setState({
          dashboards: {
            defaultDashboards: response.data.defaultDashboards || {},
            teamDashboards: response.data.teamDashboards || []
          },
          loading: false
        });
      } else {
        setState({
          dashboards: { defaultDashboards: {}, teamDashboards: [] },
          loading: false
        });
      }
    } catch (error) {
      console.error('Error fetching web teams dashboards:', error);
      setState({
        dashboards: { defaultDashboards: {}, teamDashboards: [] },
        loading: false
      });
    }
  }, []);

  useEffect(() => {
    fetchData({ silent: false });
  }, [fetchData]);

  // Prune assignments of teams deleted from the platform (backend sync)
  useDeletedWebTeamsCleanup(state.dashboards?.teamDashboards, fetchData);

  return {
    dashboardsById,
    dashboards: state.dashboards,
    loading: state.loading,
    refetchDashboards,
    refetchTeamDashboards: fetchData
  };
}

/**
 * Hook to fetch and normalize WebTeams launchers data (V2 API)
 * Fetches both the actual launchers and their team assignments,
 * and cleans up assignments for teams deleted from the platform.
 */
export function useWebTeamsLaunchers() {
  // Get all launchers via feature registry
  const { itemsById: launchersById } = useFeatureListData(FEATURE_KEYS.LAUNCH_BAR);

  const [state, setState] = useState({
    launchers: null,
    loading: true
  });

  const fetchData = useCallback(async ({ silent = true } = {}) => {
    if (!silent) setState((prev) => ({ ...prev, loading: true }));
    try {
      const response = await API_GET_WEB_TEAMS_LAUNCHBAR_V2();

      if (response?.data) {
        setState({
          launchers: {
            defaultLauncher: response.data.defaultLauncher || {},
            teamLaunchers: response.data.teamLaunchers || []
          },
          loading: false
        });
      } else {
        setState({
          launchers: { defaultLauncher: {}, teamLaunchers: [] },
          loading: false
        });
      }
    } catch (error) {
      console.error('Error fetching web teams launchers:', error);
      setState({
        launchers: { defaultLauncher: {}, teamLaunchers: [] },
        loading: false
      });
    }
  }, []);

  useEffect(() => {
    fetchData({ silent: false });
  }, [fetchData]);

  // Prune assignments of teams deleted from the platform (backend sync)
  useDeletedWebTeamsCleanup(state.launchers?.teamLaunchers, fetchData);

  return {
    launchersById,
    launchers: state.launchers,
    loading: state.loading,
    refetchTeamLaunchers: fetchData
  };
}
